<script setup lang="ts">
import { computed } from 'vue';
import ActivityFeedSocial from './ActivityFeedSocial.vue';
import type { ActivityEntityType } from '@/types/activityFeed';
import Card from 'primevue/card';
import Button from 'primevue/button';
import { useRouter } from 'vue-router';

interface Props {
    title?: string;
    subtitle?: string;
    height?: string | number;
    limit?: number;
    entityId?: string;
    entityType?: ActivityEntityType;
    entityTypes?: ActivityEntityType[];
    showAll?: boolean;
    compact?: boolean;
    showHeader?: boolean;
    showHeaderActions?: boolean;
    showEmpty?: boolean;
    autoRefresh?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    title: 'Activity Feed',
    height: 'auto',
    limit: 20,
    showAll: true,
    compact: false,
    showHeader: true,
    showHeaderActions: true,
    showEmpty: true,
    autoRefresh: true
});

const router = useRouter();

// Computed height for the feed
const feedHeight = computed(() => {
    if (props.height === 'auto') {
        return props.showHeader ? 'calc(100% - 80px)' : '100%';
    }
    if (typeof props.height === 'number') {
        return `${props.height}px`;
    }
    return props.height;
});

// Computed to check if we're filtering
const isFiltered = computed(() => {
    return !!(props.entityId || props.entityType || props.entityTypes?.length);
});

// Navigate to full activity view
const viewAll = () => {
    router.push('/activity');
};

// Refresh the feed (will be handled by the child component)
const refresh = () => {
    // This will trigger a re-render of the ActivityFeedSocial component
    // The component will handle the refresh internally
};
</script>

<template>
    <Card 
        class="activity-feed-card"
        :class="{ 'compact': compact, 'no-header': !showHeader }"
    >
        <template v-if="showHeader" #header>
            <div class="card-header">
                <div class="header-content">
                    <h3 class="card-title">{{ title }}</h3>
                    <p v-if="subtitle" class="card-subtitle">{{ subtitle }}</p>
                </div>
                <div v-if="showHeaderActions" class="header-actions">
                    <Button 
                        icon="pi pi-refresh"
                        text
                        rounded
                        size="small"
                        @click="refresh"
                        v-tooltip="'Refresh'"
                        class="refresh-btn"
                    />
                    <Button 
                        v-if="!isFiltered"
                        icon="pi pi-external-link"
                        text
                        rounded
                        size="small"
                        @click="viewAll"
                        v-tooltip="'View all activities'"
                        class="view-all-btn"
                    />
                </div>
            </div>
        </template>

        <template #content>
            <div 
                class="feed-container"
                :style="{ height: feedHeight }"
            >
                <ActivityFeedSocial
                    :max-height="feedHeight"
                    :limit="limit"
                    :entity-id="entityId"
                    :entity-type="entityType"
                    :entity-types="entityTypes"
                    :key="`feed-${Date.now()}`"
                />
            </div>
        </template>
    </Card>
</template>

<style scoped>
.activity-feed-card {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.activity-feed-card :deep(.p-card-body) {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0;
}

.activity-feed-card :deep(.p-card-content) {
    flex: 1;
    padding: 0;
    height: 100%;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--surface-200);
}

.header-content {
    flex: 1;
}

.card-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-color);
}

.card-subtitle {
    margin: 0.25rem 0 0 0;
    font-size: 0.875rem;
    color: var(--text-color-secondary);
}

.header-actions {
    display: flex;
    gap: 0.5rem;
    margin-left: 1rem;
}

.refresh-btn,
.view-all-btn {
    width: 2rem;
    height: 2rem;
}

.feed-container {
    height: 100%;
    overflow: hidden;
}

/* Compact mode */
.activity-feed-card.compact {
    .card-title {
        font-size: 1rem;
    }
    
    .card-subtitle {
        font-size: 0.75rem;
    }
    
    .card-header {
        padding: 0.75rem 1rem;
    }
}

/* No header mode */
.activity-feed-card.no-header {
    :deep(.p-card-header) {
        display: none;
    }
}

/* Dark mode adjustments */
:root[class*='dark'] {
    .card-header {
        border-bottom-color: var(--surface-700);
    }
}

/* Responsive */
@media (max-width: 768px) {
    .card-header {
        padding: 0.75rem 1rem;
    }
    
    .card-title {
        font-size: 1.125rem;
    }
    
    .header-actions {
        margin-left: 0.5rem;
    }
}
</style>